import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Product, PRODUCT_MEDIA_TYPE } from '@b2b/features/product/core/utilities/product.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, debounceTime, distinctUntilChanged, Observable, tap, map } from 'rxjs';
import { Suggestions } from '@b2b/features/product/core/utilities/suggestion.model';
import { SuggestionsService } from '@b2b/features/product/core/services/suggestions.service';
import { MediaComponent } from '@shared/components/media/media.component';
import { ProductImageService } from '@b2b/features/product/core/services/product-image.service';

@Component({
  selector: 'cx-searchable',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, MediaComponent],
  templateUrl: './searchable.component.html',
  styleUrls: ['./searchable.component.scss'],
})
export class SearchableComponent implements OnInit {
  private readonly productImageService = inject(ProductImageService);

  #destroyRef = inject(DestroyRef);
  #translate = inject(TranslateService);
  #router = inject(Router);
  #suggestionsService = inject(SuggestionsService);
  #activatedRoute = inject(ActivatedRoute);

  form = new FormGroup({
    value: new FormControl(''),
  });

  productResults$ = new BehaviorSubject<Suggestions | undefined>(undefined);
  showResults = false;
  showNoResults = false;
  loading: boolean = false;

  private searchTerm$ = new BehaviorSubject<string | undefined>(undefined);

  placeholder$: Observable<string> = this.#translate.get('searchable.placeholder.products');

  imageNotFound = this.productImageService.getImageNotFound(PRODUCT_MEDIA_TYPE.PRODUCT);

  imageProduct = (product: Product) => {
    if (!product) return this.imageNotFound;
    return this.productImageService.getImage(product, PRODUCT_MEDIA_TYPE.PRODUCT);
  };

  onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = this.imageNotFound.src;
  }

  ngOnInit() {
    this.setupFormSubscriptions();
  }

  private setupFormSubscriptions(): void {
    this.#activatedRoute.queryParams
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        map((params: Params) => {
          const query = params['query'];
          return query ? (query as string).replace(/:.*$/, '') : '';
        }),
      )
      .subscribe(value => {
        this.form.patchValue({ value });
      });

    this.searchTerm$
      ?.pipe(
        tap(value => (this.loading = !!value)),
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((searchTerm: string | undefined) => {
        const minLength = 3;
        if (searchTerm && searchTerm.length >= minLength) {
          this.searchProducts(searchTerm);
          this.showResults = true;
          this.loading = false;
        } else {
          this.clearResults();
          this.showResults = false;
          this.loading = false;
        }
      });
  }
  onSearch(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    this.searchTerm$.next(inputValue);
  }

  private searchProducts(searchTerm: string): void {
    if (searchTerm.length < 3) {
      this.productResults$.next(undefined);
      this.showNoResults = false;
      return;
    }

    this.#suggestionsService.suggestionsProducts(searchTerm).subscribe({
      next: suggestions => {
        this.productResults$.next(suggestions);
        this.showNoResults = suggestions.products.length === 0;
      },
      error: _error => {
        this.productResults$.next(undefined);
        this.showNoResults = true;
      },
    });
  }

  private clearResults(): void {
    this.productResults$.next(undefined);
    this.showNoResults = false;
  }

  navigateToSuggestion(searchValue: string): void {
    this.#router.navigate(['/pesquisa'], {
      queryParams: { query: searchValue + ':score', page: 1 },
    });
    this.form.get('value')?.setValue('');
    this.clearResults();
    this.showResults = false;
  }

  selectProduct(product: Product): void {
    this.#router.navigate(['/produto', product.code, product.name]);
    this.form.get('value')?.setValue('');
    this.clearResults();
    this.showResults = false;
  }

  hideResults(): void {
    setTimeout(() => {
      this.showResults = false;
    }, 200);
  }

  onInputFocus(): void {
    if ((this.productResults$?.value?.products?.length ?? 0) > 0) {
      this.showResults = true;
    }
  }

  onSubmit() {
    const searchValue = this.form.get('value')?.value;

    if (searchValue) {
      this.#router.navigate(['/pesquisa'], {
        queryParams: { query: searchValue + ':score', page: 1 },
      });
    }

    this.clearResults();
    this.showResults = false;
  }

  trackByProductCode(index: number, product: Product): string {
    return product.code || index.toString();
  }

  trackBySuggestion(index: number, suggestion: any): any {
    return suggestion || index.toString();
  }
}
